import { atom, type Getter, getDefaultStore } from "jotai";
import { atomWithStorage } from "jotai/utils";
import React from "react";
import { omit } from "remeda";
import { safeJSONStorage, safeLocalStorage, safeSessionStorage } from "utils/safeStorage";

const defaultStore = getDefaultStore();

export const drawerAtom = atom(false);
// Open or closed survives a reload. One flag for every location, and not synced across tabs: closing the dock in one
// tab never shuts it in another.
export const agentChatSidebarAtom = atomWithStorage(
  "qwick-ai-chat-dock-open",
  false,
  omit(safeJSONStorage<boolean>(), ["subscribe"]),
  { getOnInit: true },
);
export const superAdminPanelAtom = atom(false);

const LOCATION_ID_STORE_KEY = "currentLocation";
const URL_LOCATION_ID_REGEX = /^\/locations\/(\d+)(?:\/|$)/;

export function parseUrlLocationId(pathname: string) {
  const match = URL_LOCATION_ID_REGEX.exec(pathname);
  if (!match) return;
  const id = Number(match[1]);
  return Number.isNaN(id) ? undefined : id;
}

export const swapLocationInPath = (pathname: string, from: number, to: number) =>
  pathname.replace(`/locations/${from}`, `/locations/${to}`);

const parseStoredNumber = (raw: string) => {
  try {
    return Number(JSON.parse(raw));
  } catch {
    return Number.NaN;
  }
};

const parseStoredLocationId = (raw: string | null) => {
  if (!raw) return;
  const parsed = parseStoredNumber(raw);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const getUrlLocationId = () =>
  globalThis.window === undefined ? undefined : parseUrlLocationId(globalThis.location.pathname);

export const activeLocationIdAtom = atomWithStorage<number | undefined>(
  LOCATION_ID_STORE_KEY,
  undefined,
  {
    getItem: (key) => {
      const fromSession = parseStoredLocationId(safeSessionStorage.getItem(key));
      if (fromSession !== undefined) return fromSession;
      const fromLocal = parseStoredLocationId(safeLocalStorage.getItem(key));
      if (fromLocal !== undefined) safeSessionStorage.setItem(key, JSON.stringify(fromLocal));
      return fromLocal;
    },
    setItem: (key, newValue) => {
      if (newValue === undefined) {
        safeSessionStorage.removeItem(key);
        safeLocalStorage.removeItem(key);
        return;
      }
      const serialized = JSON.stringify(newValue);
      safeSessionStorage.setItem(key, serialized);
      safeLocalStorage.setItem(key, serialized);
    },
    removeItem: (key) => {
      safeSessionStorage.removeItem(key);
      safeLocalStorage.removeItem(key);
    },
  },
  { getOnInit: true },
);

export const readActiveLocationId = (get: Getter) => getUrlLocationId() ?? get(activeLocationIdAtom);

const renderProbe = React.createContext(null);

function isRendering() {
  const dispatcher = Reflect.get(
    Reflect.get(React, "__CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE") ?? {},
    "H",
  );
  if (!dispatcher) return false;
  try {
    dispatcher.use(renderProbe);
    return true;
  } catch {
    return false;
  }
}

function assertNotRendering(name: string) {
  if (import.meta.env.DEV && isRendering()) {
    throw new Error(
      `${name}() was called during render. React Compiler caches the result, so it keeps the location from the first render. ` +
        "Use useActiveLocationId(), or a <Link> to `/locations/$locationId/...` without params (it inherits the URL's).",
    );
  }
}

/**
 * ⚠️ NOT FOR RENDER. Snapshot for event handlers, query fns, loaders and interceptors only.
 * In a component or hook use `useStoredLocationId()`; dev builds throw when this runs during render.
 */
export const getStoredLocationId = () => {
  assertNotRendering("getStoredLocationId");
  const value = defaultStore.get(activeLocationIdAtom);
  return Number.isNaN(value) ? undefined : value;
};

/**
 * ⚠️ NOT FOR RENDER. Snapshot for event handlers, query fns, loaders and interceptors only.
 * In a component or hook use `useActiveLocationId()`; dev builds throw when this runs during render.
 */
export const getActiveLocationId = () => {
  assertNotRendering("getActiveLocationId");
  return getUrlLocationId() ?? getStoredLocationId();
};
